/* 
	This is the Initializer class. It was created to handle the way K12 admin loads front-end components.
	Since components in the K12 admin are loaded via AJAX, methods attached to the document ready event
	fail because they have already run by the time the component is added to the page. 
	For more on ES6 Classes, see: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Classes
*/
class Initializer {
	/* 
		This is the constructor for the class, and it is run for each new instance of the class.
		It checks to see if the HTML element has the class 'kentico-page-builder'. If you notice
		the admin isn't working, check with the back-end dev to make sure they are adding the class
		in page builder mode. This function uses a mutation observer to watch for elements added or 
		removed from the HTML element, when something is added it will re-run initialization scripts.
	*/
	constructor() {
		// Check to see if the HTML element (which exists on page load), has the kentico-page-builder class.
		// If it does, create a mutation observer for the HTML.kentico-page-builder element.
		// For more on mutationObservers see: https://developer.mozilla.org/en-US/docs/Web/API/MutationObserver
		if (document.querySelector('html').classList.contains('kentico-page-builder')) {
			// Assign the HTML element to the variable 'target'.
			const target = document.querySelector('html')
			// Define the config for the mutation observer. We are going to watch for elements added and removed, and changes to those elements
			const config = { childList: true, subtree: true }
			// Define the function that runs when a mutation is detected.
			const callback = (ml, ob) => {
				// Ml = mutation list, an array of the different mutations detected. Loop through each of them
				for (let m of ml) {
					// Check if the mutation detected has the type of 'childList' meaning a child was added or removed.
					if (m.type == 'childList') {
						// A child was added or removed, run the _initAll() method.
						this._initAll()
					}
				}
			}
			// Create a new observer, and pass it our call back function
			const observer = new MutationObserver(callback)
			// Direct the observer to observe the target with the given configuration
			observer.observe(target, config)
		}
	}
	/*
		This is a getter that lets you see all the methods you have added to the init class. If you 
		want to check, go to the console and type blue.init._methods, then hit enter. It will list off all
		the methods you have created.
	*/
	get _methods() {
		// Loop through each prop in the class.
		for (let prop in this) {
			// Check if the prop starts with an '_'. If not, log the name of the function to the console.
			if (prop[0] != '_' && prop != 'ready') {
				console.log(`${prop}()`)
			}
		}
	}
	/*
		This is the function that is run when a new element is added to the page in page builder mode.
		It re-fires all of the functions that have been added to the Initializer class w/ the ready function.
	*/
	_initAll = () => {
		// Loop through each prop in the class.
		for (let prop in this) {
			// Check if the prop starts with an '_'. If not, run the function.
			if (prop[0] != '_' && prop != 'ready') {
				this[prop]()
			}
		}
	}
	/*
		This is the _addMethod method. It adds new methods to the class so they can be stored and run again
		when elements are added/removed from the DOM. When initializer functions are run, they will add an array
		called _init to each element it is run on, and add the name of the function to the array. Before it runs
		the function, it will check the array or each specific element it is running on to see if it has already
		been initialized. This will prevent repeat initializations on the same element, and allows us to reinitialize
		everything each time something is added to the page w/o duplication of scripts. 
	*/
	_addMethod = (name, selector, callback) => {
		// Check to make sure you arent trying to create a new function with a name that already exists. Throw an error if it does.
		if (this.hasOwnProperty(name)) {
			console.error(`The method ${name}() has already been defined. Please define a different name.`)
		}
		// Function name has not already been defined.
		else {
			// Check to make sure the function you are creating doesn't start with an '_', that is reserved for internal methods.
			if (name[0] == "_") {
				console.error(`You are trying to define an initializer callback that starts with an underscore: '${name}()'. This is not allowed, please define a different name.`)
			}
			// Function is a unique name and doesn't start with an '_', create the method.
			else {
				// create a new method in this class with the name you passed to the ready function.
				this[name] = () => {
					// Find all elements on the page that match the selector you passed to the ready function.
					const elems = [...document.querySelectorAll(selector)]
					// Loop through each of the matching elements one at a time.
					for (let elem of elems) {
						// Check to see if this individual element has the _init array, if not create it.
						if (!elem._init) {
							elem._init = []
						}
						// Check to see if the name of this function exists in the _init array (i.e. its already initialized). If not, initialize the element.
						if (!elem._init.includes(name)) {
							// Pass the element back to the call back function.
							callback(elem)
							// Push the name of this function to the element's _init array to indicate it has been initialized.
							elem._init.push(name)
						}
					}
				}
			}
		}
	}
	/*
		This is the ready function that we use when writing scripts. It takes a name (this will be used to name your callback).
		a selector (CSS selector - matching elements will passed back to the callback), and a callback (the function that will
		be run for each matching element).
	*/
	ready = (name, selector, callback) => {
		// Pass the name, selector, and callback to the init._addMethod() method, to create a new method in the init instance of the Initializer class.
		this._addMethod(name, selector, callback)
		// Add a document ready event that triggers the callback (now stored inside of init). This is what runs normally on the page, when not in editor mode.
		document.addEventListener("DOMContentLoaded", this[name])
	}
};

export class Blue {
	constructor(props = {}) {
		Object.assign(this, props)
		this.init = new Initializer()
		this.ready = this.init.ready
	}
}
window.Blue = Blue

const props = {
	comp: {},
	util: {}
}
const blue = new Blue(props)
window.blue = blue

export const comp = blue.comp
export const util = blue.util
export const ready = blue.ready
export const init = blue.init

export default blue